import { useState, useEffect, useCallback } from 'react';
import { Header } from '../components/dashboard/Header';
import { MetricCard, StatusCard } from '../components/dashboard/MetricCard';
import { M365Section } from '../components/dashboard/M365Section';
import { OperationsSection } from '../components/dashboard/OperationsSection';
import { GoalsTracker } from '../components/dashboard/GoalsTracker';
import { MetricWizard } from '../components/dashboard/MetricWizard';
import { InternalServicesSection } from '../components/dashboard/InternalServicesSection';
import { ExternalServicesSection } from '../components/dashboard/ExternalServicesSection';
import { EverWatchSection } from '../components/dashboard/EverWatchSection';
import { AdditionalExpensesSection } from '../components/dashboard/AdditionalExpensesSection';
import { ProjectsSection } from '../components/dashboard/ProjectsSection';
import { SettingsDialog } from '../components/dashboard/SettingsDialog';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  getDashboardSummary,
  getLicenses,
  getStorage,
  getIdentityStats,
  getAzureSpend,
  getAzureCostsAllScopes,
  getGovernance,
  getOperations,
  getServicesHealth,
  getInternalServices,
  addInternalService,
  updateInternalService,
  deleteInternalService,
  checkInternalService,
  checkAllInternalServices,
  getGoals,
  createGoal,
  deleteGoal,
  createCustomMetric,
  getExternalServices,
  getJiraSatisfaction,
  getProjects,
} from '../lib/api';
import {
  Building2,
  Activity,
  Shield,
  DollarSign,
  Target,
  Server,
  ChevronDown,
  ChevronUp,
  Eye,
  Globe,
  RefreshCw,
  HeartPulse,
  Ticket,
  Users,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  ThumbsUp,
  FolderKanban,
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get Azure Management token from MSAL
const getAzureManagementToken = async () => {
  try {
    const { PublicClientApplication } = await import('@azure/msal-browser');
    const { msalConfig, azureManagementRequest } = await import('../config/authConfig');
    
    const msalInstance = new PublicClientApplication(msalConfig);
    await msalInstance.initialize();
    
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      console.warn('No MSAL accounts found for Azure token');
      return null;
    }
    
    try {
      const response = await msalInstance.acquireTokenSilent({
        ...azureManagementRequest,
        account: accounts[0],
      });
      return response.accessToken;
    } catch (silentError) {
      console.warn('Silent Azure token acquisition failed:', silentError);
      return null;
    }
  } catch (err) {
    console.error('Failed to get Azure Management token:', err);
    return null;
  }
};

// Component to display 2026 Goals progress from localStorage
const GoalsProgressDisplay = () => {
  const [progress, setProgress] = useState({ percent: 0, completed: 0, total: 0 });
  
  useEffect(() => {
    const calculateProgress = () => {
      try {
        const saved = localStorage.getItem('it_goals_2026');
        if (saved) {
          const topics = JSON.parse(saved);
          const totalInitiatives = topics.reduce((sum, t) => sum + (t.initiatives?.length || 0), 0);
          const completedInitiatives = topics.reduce(
            (sum, t) => sum + (t.initiatives?.filter(i => i.status === 'complete').length || 0),
            0
          );
          const percent = totalInitiatives > 0 ? Math.round((completedInitiatives / totalInitiatives) * 100) : 0;
          setProgress({ percent, completed: completedInitiatives, total: totalInitiatives });
        }
      } catch {
        // Ignore parse errors
      }
    };
    
    // Calculate on mount
    calculateProgress();
    
    // Listen for storage changes (in case goals are updated in another tab)
    window.addEventListener('storage', calculateProgress);
    
    // Also poll periodically for same-tab updates
    const interval = setInterval(calculateProgress, 2000);
    
    return () => {
      window.removeEventListener('storage', calculateProgress);
      clearInterval(interval);
    };
  }, []);
  
  const getColor = () => {
    if (progress.percent >= 70) return 'text-[rgb(var(--success))]';
    if (progress.percent >= 40) return 'text-[rgb(var(--warning))]';
    return 'text-primary';
  };
  
  return (
    <>
      <p className={cn("text-4xl font-bold tracking-tight mb-2", getColor())} style={{ fontFamily: 'Chivo, sans-serif' }}>
        {progress.percent}%
      </p>
      <p className="text-sm text-muted-foreground">
        {progress.completed}/{progress.total} initiatives
      </p>
      <div className="mt-2 h-1.5 bg-muted/30 rounded-full overflow-hidden">
        <div 
          className={cn(
            "h-full rounded-full transition-all duration-500",
            progress.percent >= 70 ? "bg-[rgb(var(--success))]" : progress.percent >= 40 ? "bg-[rgb(var(--warning))]" : "bg-primary"
          )}
          style={{ width: `${progress.percent}%` }}
        />
      </div>
    </>
  );
};

export default function Dashboard({ onShowAdmin, isReadOnly = false, viewerEmail }) {
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [timeRange, setTimeRange] = useState('7 days');
  
  // Loading states for each section
  const [loadingStates, setLoadingStates] = useState({
    summary: true,
    m365: true,
    azure: true,
    services: true,
    internalServices: true,
    operations: true,
    externalServices: true,
    goals: true,
  });
  
  // Data states
  const [summary, setSummary] = useState(null);
  const [licenses, setLicenses] = useState([]);
  const [storage, setStorage] = useState(null);
  const [identity, setIdentity] = useState(null);
  const [azureSpend, setAzureSpend] = useState(null);
  const [azureTotalCost, setAzureTotalCost] = useState(0);
  const [governance, setGovernance] = useState(null);
  const [operations, setOperations] = useState(null);
  const [satisfaction, setSatisfaction] = useState(null);
  const [serviceHealth, setServiceHealth] = useState([]);
  const [internalServices, setInternalServices] = useState([]);
  const [goals, setGoals] = useState([]);
  const [externalServices, setExternalServices] = useState(null);
  const [projectStats, setProjectStats] = useState({ active: 0, total: 0 });

  // Collapsible sections - start collapsed
  const [sectionsOpen, setSectionsOpen] = useState({
    m365: false,
    azure: false,
    services: false,
    internalServices: false,
    externalServices: false,
    expenses: false,
    everwatch: false,
    operations: false,
    goals: false,
    projects: false,
  });

  const toggleSection = (section) => {
    setSectionsOpen(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Convert time range to days
  const getTimeRangeDays = useCallback(() => {
    switch (timeRange) {
      case '7 days': return 7;
      case '30 days': return 30;
      case '90 days': return 90;
      default: return 30;
    }
  }, [timeRange]);

  // Load sections independently for faster perceived performance
  const fetchData = useCallback(async () => {
    const days = getTimeRangeDays();
    setIsLoading(false); // Show page immediately
    
    // Load Executive Overview first (most important)
    getDashboardSummary()
      .then(res => {
        setSummary(res.data);
        setLoadingStates(prev => ({ ...prev, summary: false }));
      })
      .catch(() => setLoadingStates(prev => ({ ...prev, summary: false })));

    // Load M365 data
    Promise.all([getLicenses(), getStorage(), getIdentityStats()])
      .then(([licensesRes, storageRes, identityRes]) => {
        const licenseData = licensesRes.data?.licenses || licensesRes.data;
        setLicenses(Array.isArray(licenseData) ? licenseData : []);
        setStorage(storageRes.data?.error ? null : storageRes.data);
        setIdentity(identityRes.data?.error ? null : identityRes.data);
        setLoadingStates(prev => ({ ...prev, m365: false }));
      })
      .catch(() => setLoadingStates(prev => ({ ...prev, m365: false })));

    // Load Azure data - get user token for cost API
    (async () => {
      try {
        // Get Azure Management token for cost API
        const azureToken = await getAzureManagementToken();
        
        // Fetch Azure costs with user token (cache warming)
        const azureCostsRes = await getAzureCostsAllScopes(30, azureToken);
        if (azureCostsRes.data && !azureCostsRes.data.error) {
          setAzureTotalCost(azureCostsRes.data.total || 0);
          console.log('[Cache Warming] Azure costs pre-fetched:', azureCostsRes.data.total);
          
          // Cache for Executive Card widgets to use
          if (azureCostsRes.data.total > 0) {
            try {
              sessionStorage.setItem('cachedAzureCosts', JSON.stringify({
                total: azureCostsRes.data.total,
                timestamp: Date.now()
              }));
            } catch (e) { /* ignore storage errors */ }
          }
        }
        
        // Also fetch other Azure data
        const [azureRes, govRes] = await Promise.all([
          getAzureSpend(),
          getGovernance(),
        ]);
        setAzureSpend(azureRes.data?.error ? null : azureRes.data);
        setGovernance(govRes.data?.error ? null : govRes.data);
        setLoadingStates(prev => ({ ...prev, azure: false }));
      } catch (error) {
        console.error('Azure data fetch error:', error);
        setLoadingStates(prev => ({ ...prev, azure: false }));
      }
    })();

    // Load Service Health
    getServicesHealth()
      .then(res => {
        const serviceData = res.data?.services || res.data;
        setServiceHealth(Array.isArray(serviceData) ? serviceData : []);
        setLoadingStates(prev => ({ ...prev, services: false }));
      })
      .catch(() => setLoadingStates(prev => ({ ...prev, services: false })));

    // Load Internal Services
    getInternalServices()
      .then(res => {
        setInternalServices(res.data);
        setLoadingStates(prev => ({ ...prev, internalServices: false }));
      })
      .catch(() => setLoadingStates(prev => ({ ...prev, internalServices: false })));

    // Load Ticket Data (can be slow due to Jira)
    getOperations(days)
      .then(res => {
        setOperations(res.data);
        setLoadingStates(prev => ({ ...prev, operations: false }));
      })
      .catch(() => setLoadingStates(prev => ({ ...prev, operations: false })));

    // Load Satisfaction Data (separate endpoint for reviews)
    getJiraSatisfaction(days)
      .then(res => {
        setSatisfaction(res.data);
      })
      .catch(() => setSatisfaction(null));

    // Load Project Stats for Executive Overview
    getProjects()
      .then(res => {
        const projects = res.data?.projects || [];
        const active = projects.filter(p => p.status !== 'completed').length;
        setProjectStats({ active, total: projects.length });
      })
      .catch(() => {});

    // Load External Services
    getExternalServices()
      .then(res => {
        setExternalServices(res.data);
        setLoadingStates(prev => ({ ...prev, externalServices: false }));
      })
      .catch(() => setLoadingStates(prev => ({ ...prev, externalServices: false })));

    // Load Goals
    getGoals()
      .then(res => {
        setGoals(res.data);
        setLoadingStates(prev => ({ ...prev, goals: false }));
      })
      .catch(() => setLoadingStates(prev => ({ ...prev, goals: false })));
      
  }, [getTimeRangeDays]);

  // Individual section refresh functions
  const refreshExecutiveOverview = async () => {
    setLoadingStates(prev => ({ ...prev, summary: true }));
    try {
      const summaryRes = await getDashboardSummary();
      setSummary(summaryRes.data);
      toast.success('Executive Overview refreshed');
    } catch (error) {
      toast.error('Failed to refresh Executive Overview');
    } finally {
      setLoadingStates(prev => ({ ...prev, summary: false }));
    }
  };

  const refreshM365 = async () => {
    setLoadingStates(prev => ({ ...prev, m365: true }));
    try {
      const [licensesRes, storageRes, identityRes] = await Promise.all([
        getLicenses(),
        getStorage(),
        getIdentityStats(),
      ]);
      const licenseData = licensesRes.data?.licenses || licensesRes.data;
      setLicenses(Array.isArray(licenseData) ? licenseData : []);
      setStorage(storageRes.data?.error ? null : storageRes.data);
      setIdentity(identityRes.data?.error ? null : identityRes.data);
      toast.success('M365 data refreshed');
    } catch (error) {
      toast.error('Failed to refresh M365 data');
    } finally {
      setLoadingStates(prev => ({ ...prev, m365: false }));
    }
  };

  const refreshAzure = async () => {
    setLoadingStates(prev => ({ ...prev, azure: true }));
    try {
      const [azureRes, govRes] = await Promise.all([
        getAzureSpend(),
        getGovernance(),
      ]);
      setAzureSpend(azureRes.data?.error ? null : azureRes.data);
      setGovernance(govRes.data?.error ? null : govRes.data);
      toast.success('Azure data refreshed');
    } catch (error) {
      toast.error('Failed to refresh Azure data');
    } finally {
      setLoadingStates(prev => ({ ...prev, azure: false }));
    }
  };

  const refreshServiceHealth = async () => {
    setLoadingStates(prev => ({ ...prev, services: true }));
    try {
      const servicesRes = await getServicesHealth();
      const serviceData = servicesRes.data?.services || servicesRes.data;
      setServiceHealth(Array.isArray(serviceData) ? serviceData : []);
      toast.success('Service health refreshed');
    } catch (error) {
      toast.error('Failed to refresh service health');
    } finally {
      setLoadingStates(prev => ({ ...prev, services: false }));
    }
  };

  const refreshTickets = async () => {
    setLoadingStates(prev => ({ ...prev, operations: true }));
    try {
      const days = getTimeRangeDays();
      // Fetch both operations and satisfaction data in parallel
      const [opsRes, satRes] = await Promise.all([
        getOperations(days),
        getJiraSatisfaction(days)
      ]);
      setOperations(opsRes.data);
      setSatisfaction(satRes.data);
      toast.success('Ticket data refreshed');
    } catch (error) {
      toast.error('Failed to refresh ticket data');
    } finally {
      setLoadingStates(prev => ({ ...prev, operations: false }));
    }
  };

  const refreshExternalServices = async () => {
    setLoadingStates(prev => ({ ...prev, externalServices: true }));
    try {
      const externalServicesRes = await getExternalServices();
      setExternalServices(externalServicesRes.data);
      toast.success('External services refreshed');
    } catch (error) {
      toast.error('Failed to refresh external services');
    } finally {
      setLoadingStates(prev => ({ ...prev, externalServices: false }));
    }
  };

  const refreshGoals = async () => {
    setLoadingStates(prev => ({ ...prev, goals: true }));
    try {
      const goalsRes = await getGoals();
      setGoals(goalsRes.data);
      toast.success('Goals refreshed');
    } catch (error) {
      toast.error('Failed to refresh goals');
    } finally {
      setLoadingStates(prev => ({ ...prev, goals: false }));
    }
  };

  const fetchInternalServices = async () => {
    try {
      const res = await getInternalServices();
      setInternalServices(res.data);
    } catch (error) {
      console.error('Failed to fetch internal services:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await fetchData();
      setIsLoading(false);
    };
    loadData();
  }, [fetchData]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchData();
    setIsRefreshing(false);
    toast.success('Dashboard refreshed');
  };

  const handleSaveMetric = async (metric) => {
    try {
      await createCustomMetric(metric);
      toast.success('Metric created successfully');
      await fetchData();
    } catch (error) {
      toast.error('Failed to create metric');
    }
  };

  // Internal Services handlers
  const handleAddInternalService = async (service) => {
    try {
      await addInternalService(service);
      await fetchInternalServices();
    } catch (error) {
      toast.error('Failed to add service');
    }
  };

  const handleUpdateInternalService = async (id, update) => {
    try {
      await updateInternalService(id, update);
      await fetchInternalServices();
    } catch (error) {
      toast.error('Failed to update service');
    }
  };

  const handleDeleteInternalService = async (id) => {
    try {
      await deleteInternalService(id);
      await fetchInternalServices();
      toast.success('Service deleted');
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  const handleCheckInternalService = async (id) => {
    try {
      await checkInternalService(id);
      await fetchInternalServices();
    } catch (error) {
      toast.error('Failed to check service');
    }
  };

  const handleCheckAllInternalServices = async () => {
    try {
      await checkAllInternalServices();
      await fetchInternalServices();
    } catch (error) {
      toast.error('Failed to check services');
    }
  };

  const handleDeleteGoal = async (goalId) => {
    try {
      await deleteGoal(goalId);
      toast.success('Goal deleted');
      const goalsRes = await getGoals();
      setGoals(goalsRes.data);
    } catch (error) {
      toast.error('Failed to delete goal');
    }
  };

  const formatLastRefresh = () => {
    if (!summary?.tenant?.last_refresh) return 'Just now';
    const date = new Date(summary.tenant.last_refresh);
    return date.toLocaleTimeString();
  };

  const formatCurrency = (value) => {
    if (!value) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Loading indicator component for sections
  const SectionLoading = ({ message = "Loading..." }) => (
    <div className="flex items-center justify-center py-12 text-muted-foreground">
      <RefreshCw className="w-5 h-5 animate-spin mr-3" />
      <span>{message}</span>
    </div>
  );

  // Initial page loading - show immediately with loading message
  if (isLoading) {
    return (
      <div className="dashboard-container bg-background min-h-screen flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 animate-spin mx-auto mb-4 text-primary" />
          <h2 className="text-xl font-semibold mb-2">Loading Dashboard</h2>
          <p className="text-muted-foreground">Please wait while we fetch your data...</p>
        </div>
      </div>
    );
  }

  // Handle notification click - scroll to relevant section
  const handleNotificationClick = (notification) => {
    const sectionMap = {
      'm365': 'm365-section-wrapper',
      'security': 'azure-section-wrapper',
      'operations': 'ticket-snapshot-section-wrapper',
      'azure': 'azure-section-wrapper',
      'projects': 'projects-section-wrapper',
      'services': 'services-section-wrapper',
      'internalServices': 'internal-services-section-wrapper',
      'externalServices': 'external-services-section-wrapper',
    };
    
    const sectionId = sectionMap[notification.section];
    if (sectionId) {
      // First expand the section if collapsed
      const sectionKey = notification.section;
      if (sectionsOpen[sectionKey] === false) {
        setSectionsOpen(prev => ({ ...prev, [sectionKey]: true }));
      }
      
      // Scroll to section after a brief delay to allow expansion
      setTimeout(() => {
        const element = document.querySelector(`[data-testid="${sectionId}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 150);
    }
  };

  return (
    <div className="dashboard-container bg-background min-h-screen" data-testid="dashboard-container">
      <Header
        tenantName={summary?.tenant?.tenant_name}
        lastRefresh={formatLastRefresh()}
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
        onOpenWizard={() => setWizardOpen(true)}
        timeRange={timeRange}
        onTimeRangeChange={setTimeRange}
        onShowAdmin={onShowAdmin}
        onOpenSettings={() => setSettingsOpen(true)}
        onNotificationClick={handleNotificationClick}
      />

      <main className="p-6 lg:p-8 space-y-8">
        {/* Executive Overview - Top Row */}
        <section data-testid="executive-overview">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-1 h-6 bg-primary rounded-full" />
            <h2 className="text-xl font-bold tracking-tight flex-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Executive Overview
            </h2>
            <Button variant="ghost" size="sm" onClick={refreshExecutiveOverview} disabled={loadingStates.summary} data-testid="refresh-executive">
              <RefreshCw className={cn("w-4 h-4", loadingStates.summary && "animate-spin")} />
            </Button>
          </div>
          
          {loadingStates.summary ? (
            <SectionLoading message="Loading executive overview..." />
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6">
            {/* Internal Services Card */}
            <div className="relative overflow-hidden rounded-xl border border-border/50 bg-card/80 backdrop-blur-sm p-6" data-testid="health-card">
              <div className="flex items-start justify-between mb-4">
                <div className="p-2.5 rounded-xl bg-primary/10">
                  <Server className="w-6 h-6 text-primary" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Internal Services</p>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-4xl font-bold text-[rgb(var(--success))]" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {summary?.it_health?.internal_up ?? 0}
                </span>
                <span className="text-2xl font-bold text-muted-foreground">/</span>
                <span className={cn(
                  "text-4xl font-bold",
                  (summary?.it_health?.internal_total - summary?.it_health?.internal_up) > 0 
                    ? "text-[rgb(var(--destructive))]" 
                    : "text-[rgb(var(--success))]"
                )} style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {(summary?.it_health?.internal_total ?? 0) - (summary?.it_health?.internal_up ?? 0)}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                Services Up / Down
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {summary?.it_health?.internal_total || 0} total monitored
              </p>
            </div>

            {/* Support Queue Card - Uses operations data from Ticket Snapshot */}
            <div className="relative overflow-hidden rounded-xl border border-border/50 bg-card/80 backdrop-blur-sm p-6" data-testid="support-card">
              <div className="flex items-start justify-between mb-4">
                <div className="p-2.5 rounded-xl bg-primary/10">
                  <Ticket className="w-6 h-6 text-primary" />
                </div>
                {(operations?.source === 'jira_live' || operations?.tickets?.source === 'jira_live') && (
                  <span className="text-xs text-[rgb(var(--success))] flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[rgb(var(--success))] animate-pulse" />
                    Live
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Support Queue</p>
              <p className="text-4xl font-bold tracking-tight text-primary mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {operations?.tickets?.totals?.open ?? operations?.totals?.open ?? (loadingStates.operations ? '...' : 'N/A')}
              </p>
              <p className="text-sm text-muted-foreground">Open tickets</p>
              <div className="flex items-center gap-2 mt-2">
                <ThumbsUp className="w-4 h-4 text-[rgb(var(--success))]" />
                <span className="text-sm">
                  {satisfaction?.data?.satisfactionPercentage !== null && satisfaction?.data?.satisfactionPercentage !== undefined
                    ? `${satisfaction.data.satisfactionPercentage}% satisfaction`
                    : 'Satisfaction: N/A'}
                </span>
              </div>
            </div>

            {/* 2026 Goals Progress Card */}
            <div className="relative overflow-hidden rounded-xl border border-border/50 bg-card/80 backdrop-blur-sm p-6" data-testid="goals-progress-card">
              <div className="flex items-start justify-between mb-4">
                <div className="p-2.5 rounded-xl bg-primary/10">
                  <Target className="w-6 h-6 text-primary" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">2026 Goals</p>
              <GoalsProgressDisplay />
            </div>

            {/* Monthly Cost Tracking Card */}
            <div className="relative overflow-hidden rounded-xl border border-border/50 bg-card/80 backdrop-blur-sm p-6" data-testid="spend-card">
              <div className="flex items-start justify-between mb-4">
                <div className="p-2.5 rounded-xl bg-primary/10">
                  <DollarSign className="w-6 h-6 text-primary" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Monthly Cost Tracking</p>
              <p className="text-4xl font-bold tracking-tight text-primary mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {formatCurrency(
                  (azureTotalCost || 0) + 
                  (summary?.monthly_spend?.licensing || 0) + 
                  (summary?.monthly_spend?.smartsheet || 0) + 
                  (summary?.monthly_spend?.other || 0)
                )}
              </p>
              <div className="space-y-1 text-xs text-muted-foreground">
                <div className="flex justify-between">
                  <span>Azure</span>
                  <span className="font-mono">{formatCurrency(azureTotalCost || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span>M365 Licensing</span>
                  <span className="font-mono">{formatCurrency(summary?.monthly_spend?.licensing || 0)}</span>
                </div>
                {summary?.monthly_spend?.smartsheet > 0 && (
                  <div className="flex justify-between">
                    <span>Smartsheet</span>
                    <span className="font-mono">{formatCurrency(summary?.monthly_spend?.smartsheet || 0)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span>Additional IT Expenses</span>
                  <span className="font-mono">{formatCurrency(summary?.monthly_spend?.other || 0)}</span>
                </div>
              </div>
            </div>

            {/* Active Projects Card */}
            <div className="relative overflow-hidden rounded-xl border border-border/50 bg-card/80 backdrop-blur-sm p-6" data-testid="projects-card">
              <div className="flex items-start justify-between mb-4">
                <div className="p-2.5 rounded-xl bg-primary/10">
                  <FolderKanban className="w-6 h-6 text-primary" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Active Projects</p>
              <p className="text-4xl font-bold tracking-tight text-primary mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {projectStats.active}
              </p>
              <p className="text-sm text-muted-foreground">Not yet complete</p>
              <p className="text-xs text-muted-foreground mt-1">
                {projectStats.total} total tracked
              </p>
            </div>
          </div>
          )}
        </section>

        {/* Microsoft 365 Section */}
        <section data-testid="m365-section-wrapper">
          <div className="flex items-center gap-2 mb-6">
            <button 
              className="flex items-center gap-2 flex-1 text-left"
              onClick={() => toggleSection('m365')}
              data-testid="m365-toggle"
            >
              <div className="w-1 h-6 bg-primary rounded-full" />
              <h2 className="text-xl font-bold tracking-tight flex-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Microsoft 365 Tenant & Licensing
              </h2>
              {sectionsOpen.m365 ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            <Button variant="ghost" size="sm" onClick={refreshM365} disabled={loadingStates.m365} data-testid="refresh-m365">
              <RefreshCw className={cn("w-4 h-4", loadingStates.m365 && "animate-spin")} />
            </Button>
          </div>
          
          {sectionsOpen.m365 && (
            loadingStates.m365 ? <SectionLoading message="Loading M365 data..." /> :
            <M365Section 
              licenses={licenses}
              storage={storage}
              identity={identity}
              onRefresh={refreshM365}
            />
          )}
        </section>

        {/* Internal Services Section */}
        <section data-testid="internal-services-section-wrapper">
          <div className="flex items-center gap-2 mb-6">
            <button 
              className="flex items-center gap-2 flex-1 text-left"
              onClick={() => toggleSection('internalServices')}
              data-testid="internal-services-toggle"
            >
              <div className="w-1 h-6 bg-primary rounded-full" />
              <h2 className="text-xl font-bold tracking-tight flex-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Internal Services
              </h2>
              <div className="flex items-center gap-2 mr-4">
                <Server className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {loadingStates.internalServices ? '...' : `${internalServices?.filter(s => s.status === 'up').length || 0}/${internalServices?.length || 0} online`}
                </span>
              </div>
              {sectionsOpen.internalServices ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
          </div>
          
          {sectionsOpen.internalServices && (
            loadingStates.internalServices ? <SectionLoading message="Loading internal services..." /> :
            <InternalServicesSection
              services={internalServices}
              onAddService={handleAddInternalService}
              onDeleteService={handleDeleteInternalService}
              onCheckService={handleCheckInternalService}
              onCheckAll={handleCheckAllInternalServices}
              onUpdateService={handleUpdateInternalService}
              onRefresh={fetchInternalServices}
            />
          )}
        </section>

        {/* Ticket Snapshot Section - Always show, display error if not working */}
        <section data-testid="ticket-snapshot-section-wrapper">
          <div className="flex items-center gap-2 mb-6">
            <button 
              className="flex items-center gap-2 flex-1 text-left"
              onClick={() => toggleSection('operations')}
              data-testid="ticket-snapshot-toggle"
            >
              <div className="w-1 h-6 bg-primary rounded-full" />
              <h2 className="text-xl font-bold tracking-tight flex-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Ticket Snapshot
              </h2>
              {operations?.source === 'jira_live' ? (
                <span className="text-xs text-[rgb(var(--success))] mr-4">Live from Jira</span>
              ) : (
                <span className="text-xs text-[rgb(var(--destructive))] mr-4">Err</span>
              )}
              {sectionsOpen.operations ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            <Button variant="ghost" size="sm" onClick={refreshTickets} disabled={loadingStates.operations} data-testid="refresh-tickets">
              <RefreshCw className={cn("w-4 h-4", loadingStates.operations && "animate-spin")} />
            </Button>
          </div>
          
          {sectionsOpen.operations && (
            loadingStates.operations ? <SectionLoading message="Loading ticket data..." /> :
            <OperationsSection operations={operations} timeRange={timeRange} satisfaction={satisfaction} />
          )}
        </section>

        {/* External Services Section */}
        <section data-testid="external-services-section-wrapper">
          <div className="flex items-center gap-2 mb-6">
            <button 
              className="flex items-center gap-2 flex-1 text-left"
              onClick={() => toggleSection('externalServices')}
              data-testid="external-services-toggle"
            >
              <div className="w-1 h-6 bg-primary rounded-full" />
              <h2 className="text-xl font-bold tracking-tight flex-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
                External Services
              </h2>
              <div className="flex items-center gap-2 mr-4">
                <Globe className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {externalServices?.smartsheet?.data_source === 'live' ? 'Connected' : 'Err'}
                </span>
              </div>
              {sectionsOpen.externalServices ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            <Button variant="ghost" size="sm" onClick={refreshExternalServices} disabled={loadingStates.externalServices} data-testid="refresh-external">
              <RefreshCw className={cn("w-4 h-4", loadingStates.externalServices && "animate-spin")} />
            </Button>
          </div>
          
          {sectionsOpen.externalServices && (
            loadingStates.externalServices ? <SectionLoading message="Loading external services..." /> :
            <ExternalServicesSection 
              externalServices={externalServices}
              onRefresh={refreshExternalServices}
            />
          )}
        </section>

        {/* Additional Expenses Section */}
        <section data-testid="additional-expenses-section-wrapper">
          <div className="flex items-center gap-2 mb-6">
            <button 
              className="flex items-center gap-2 flex-1 text-left"
              onClick={() => toggleSection('expenses')}
              data-testid="expenses-toggle"
            >
              <div className="w-1 h-6 bg-primary rounded-full" />
              <h2 className="text-xl font-bold tracking-tight flex-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Additional IT Expenses
              </h2>
              {sectionsOpen.expenses ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
          </div>
          
          {sectionsOpen.expenses && (
            <AdditionalExpensesSection />
          )}
        </section>

        {/* EverWatch Technologies Section */}
        <section data-testid="everwatch-section-wrapper">
          <div className="flex items-center gap-2 mb-6">
            <button 
              className="flex items-center gap-2 flex-1 text-left"
              onClick={() => toggleSection('everwatch')}
              data-testid="everwatch-toggle"
            >
              <div className="w-1 h-6 bg-primary rounded-full" />
              <h2 className="text-xl font-bold tracking-tight flex-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
                EverWatch Technologies
              </h2>
              <div className="flex items-center gap-2 mr-4">
                <Eye className="w-4 h-4 text-muted-foreground" />
              </div>
              {sectionsOpen.everwatch ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
          </div>
          
          {sectionsOpen.everwatch && (
            <EverWatchSection timeRange={timeRange} />
          )}
        </section>

        {/* Project Management Section */}
        <section data-testid="projects-section-wrapper">
          <div className="flex items-center gap-2 mb-6">
            <button 
              className="flex items-center gap-2 flex-1 text-left"
              onClick={() => toggleSection('projects')}
              data-testid="projects-toggle"
            >
              <div className="w-1 h-6 bg-primary rounded-full" />
              <h2 className="text-xl font-bold tracking-tight flex-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Project Management
              </h2>
              <div className="flex items-center gap-2 mr-4">
                <FolderKanban className="w-4 h-4 text-muted-foreground" />
              </div>
              {sectionsOpen.projects ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
          </div>
          
          {sectionsOpen.projects && (
            <ProjectsSection />
          )}
        </section>

        {/* 2026 Goals Section */}
        <section data-testid="goals-section-wrapper">
          <div className="flex items-center gap-2 mb-6">
            <button 
              className="flex items-center gap-2 flex-1 text-left"
              onClick={() => toggleSection('goals')}
              data-testid="goals-toggle"
            >
              <div className="w-1 h-6 bg-primary rounded-full" />
              <h2 className="text-xl font-bold tracking-tight flex-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
                2026 Goals & Metrics
              </h2>
              <div className="flex items-center gap-2 mr-4">
                <Target className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {goals?.length || 0} goals
                </span>
              </div>
              {sectionsOpen.goals ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            <Button variant="ghost" size="sm" onClick={refreshGoals} disabled={loadingStates.goals} data-testid="refresh-goals">
              <RefreshCw className={cn("w-4 h-4", loadingStates.goals && "animate-spin")} />
            </Button>
          </div>
          
          {sectionsOpen.goals && (
            loadingStates.goals ? <SectionLoading message="Loading goals..." /> :
            <GoalsTracker 
              goals={goals}
              onDelete={handleDeleteGoal}
              onRefresh={refreshGoals}
            />
          )}
        </section>
      </main>

      {/* Metric Wizard Modal */}
      <MetricWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        onSave={handleSaveMetric}
      />

      {/* Settings Dialog */}
      <SettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
      />
    </div>
  );
}
